import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { getRoleLevelFromRole } from '../utils/roleUtils';
import { login as authLogin } from '../utils/auth';

export const AuthContext = createContext({
    user: null,
    login: () => { },
    logout: () => { },
    updateUser: () => { }
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const auth = localStorage.getItem('auth');
            if (auth) {
                const { user } = JSON.parse(auth);
                if (!user.roleLevel) {
                    user.roleLevel = getRoleLevelFromRole(user.role);
                }
                return user;
            }
            return null;
        } catch (error) {
            console.error('Auth context initialization error:', error);
            localStorage.removeItem('auth');
            localStorage.removeItem('token');
            localStorage.removeItem('adminSessionId');
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        console.log('AuthContext useEffect 실행');
        const loadUser = async () => {
            try {
                const auth = localStorage.getItem('auth');
                if (auth) {
                    const { token, user: storedUser } = JSON.parse(auth);
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    if (!user) {
                        setUser({
                            ...storedUser,
                            roleLevel: getRoleLevelFromRole(storedUser.role)
                        });
                    }
                    const response = await api.get('/auth/user');
                    const userData = response.data;
                    setUser({
                        ...userData,
                        // roleLevel이 없는 경우에만 계산 (하위 호환성)
                        roleLevel: userData.roleLevel || getRoleLevelFromRole(userData.role)
                    });
                }
            } catch (error) {
                console.log('auth/user 요청 실패:', error);
                if (error.response && [401, 403].includes(error.response.status)) {
                    console.log('인증 에러로 auth 삭제');
                    localStorage.removeItem('auth');
                    setUser(null);
                } else {
                    console.error('사용자 정보 로드 실패:', error);
                }
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, []);

    const handleLogin = async (credentials) => {
        try {
            const result = await authLogin(credentials);
            if (result.success) {
                // 백엔드 응답의 user 데이터 사용 (roleLevel 포함)
                const userData = {
                    ...result.user,
                    // roleLevel이 없는 경우에만 계산 (하위 호환성)
                    roleLevel: result.user.roleLevel || getRoleLevelFromRole(result.user.role)
                };

                setUser(userData);

                localStorage.setItem('auth', JSON.stringify({
                    token: result.token,
                    user: userData
                }));

                api.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
            }
            return result;
        } catch (error) {
            console.error('로그인 처리 중 오류:', error);
            throw error;
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('auth');
        localStorage.removeItem('adminSessionId');
        api.defaults.headers.common['Authorization'] = '';
        setUser(null);
    };

    const updateUser = async () => {
        try {
            const response = await api.get('/auth/user');
            const updatedUserData = {
                ...response.data,
                // roleLevel이 없는 경우에만 계산 (하위 호환성)
                roleLevel: response.data.roleLevel || getRoleLevelFromRole(response.data.role)
            };

            setUser(updatedUserData);

            const currentAuth = JSON.parse(localStorage.getItem('auth'));
            if (currentAuth) {
                localStorage.setItem('auth', JSON.stringify({
                    ...currentAuth,
                    user: updatedUserData
                }));
            }

            return updatedUserData;
        } catch (error) {
            console.error('사용자 정보 업데이트 실패:', error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login: handleLogin,
            logout: handleLogout,
            updateUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};
